import Phaser from 'phaser';
import { SCENE } from '../config.js';
import { gameState } from '../state/gameState.js';
import { MoneyBar } from '../ui/MoneyBar.js';
import { attachFriendsButton } from '../ui/FriendsButton.js';
import { WalkController } from '../ui/WalkController.js';
import { NatalkaSprite, NATALKA_SIZE } from '../ui/NatalkaSprite.js';
import { reportLocation } from '../ui/locationTracker.js';
import { attachCoordsDebug } from '../ui/CoordsDebug.js';
import { attachSceneEditMode } from '../ui/SceneEditMode.js';
import { realtime } from '../api/realtime.js';
import { attachRemotePresence } from '../ui/remotePresence.js';
import { attachRoomChat } from '../ui/ChatInput.js';
import { applyForeground } from './sceneForeground.js';
import { fitCharacter } from '../ui/outdoorScale.mjs';
import { ROOM_PERSPECTIVE } from './roomPerspective.mjs';
import { ySortDepth } from '../ui/ySort.js';
import { virtualInput } from '../ui/virtualInput.js';
import { isInputLocked } from '../ui/inputLock.js';
import { GameHallTables } from '../ui/GameHallTables.js';
import { UP_FLOOR, UP_OBSTACLES, UP_STAIRS_ZONE, UP_TABLES, feetInAnyRect, rectContains } from './gameHallLayout.js';
import { queueMissing, loadCtx, prioritiseFor } from '../ui/sceneLoading.js';
import { needsFor } from '../assetManifest.mjs';
import { renderBackground } from '../render/world.js';

const HALF_H = NATALKA_SIZE.h / 2;
const ROOM = 'game_hall:up';

export class GameHallUpstairsScene extends Phaser.Scene {
  constructor() { super(SCENE.GAME_HALL_UP); }

  preload() {
    // Staged loading: only what this scene draws now, only if the cache lacks it.
    queueMissing(this, needsFor(this.scene.key, loadCtx()));
    prioritiseFor(this.scene.key);
  }

  create(data) {
    this._spawn = { x: data?.spawnX ?? 340, y: data?.spawnY ?? 580 };
    // Saved positions can land inside obstacle rects after geometry refinements — rescue
    // to the scene default instead of softlocking (WalkController has no unstick).
    const FALLBACK = { x: 340, y: 580 };
    if (feetInAnyRect(UP_OBSTACLES, this._spawn.x, this._spawn.y + NATALKA_SIZE.h / 2)) {
      this._spawn = { ...FALLBACK };
    }
    renderBackground(this, 'bg_game_hall_up');

    this.perspective = ROOM_PERSPECTIVE.game_hall_up;   // smaller and slower towards the far edge
    this.natalka = new NatalkaSprite(this, this._spawn.x, this._spawn.y);
    this.natalka.setOutfit(gameState.raw?.inventory?.equipped);
    this.walker = new WalkController(this, this.natalka, {
      floorPolyline: UP_FLOOR,
      blocked: (fx, fy) => feetInAnyRect(UP_OBSTACLES, fx, fy),
      onBroadcast: (p) => realtime.move(p),
    });
    fitCharacter(this);
    reportLocation(this, this.natalka.x, this.natalka.y, { immediate: true });

    // Re-drawn bg polygons that occlude avatars standing behind furniture
    // (FOREGROUND_BY_SCENE['game_hall_up'] — authored in Task 5).
    this._fg = applyForeground(this, 'game_hall_up', 'bg_game_hall_up');
    this.events.on('shutdown', () => this._fg?.destroy());

    new MoneyBar(this);
    attachFriendsButton(this);

    this._unsub = gameState.on(() => this.natalka?.setOutfit(gameState.raw?.inventory?.equipped));
    this.events.on('shutdown', () => this._unsub?.());

    attachCoordsDebug(this);
    if (import.meta.env?.DEV) attachSceneEditMode(this);  // press E: author floor/foreground

    this._presence = attachRemotePresence(this, ROOM);
    this._roomChat = attachRoomChat(this, ROOM);
    this.tablesCtl = new GameHallTables(this, { tables: UP_TABLES, presence: this._presence, floorRoom: ROOM });
    if (import.meta.env?.DEV) {
      // Press G: hall authoring tool (seat anchors, felt boxes, per-game board overrides).
      import('../ui/GameHallEditMode.js').then(({ attachGameHallEditMode }) =>
        attachGameHallEditMode(this, {
          tables: UP_TABLES, floor: 'up',
          obstacles: UP_OBSTACLES, floorLine: UP_FLOOR,
        }));
    }

    // Desktop affordance for the seat/stand/menu action (mirrors GameHallScene's SPACE key).
    this._spaceKey = this.input.keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.SPACE);

    // Don't re-trigger the stairs zone right after spawning next to it.
    this._zoneGuardUntil = Date.now() + 600;
    this._exitTriggered = false;
  }

  update(_t, dt) {
    this.walker.update(dt);
    fitCharacter(this);
    reportLocation(this, this.natalka.x, this.natalka.y);
    this._depthSort();

    const fx = this.natalka.x, fy = this.natalka.feetY;

    // Seats / nameplates / tabletop games + sit/stand/host action.
    this.tablesCtl.update(fx, fy);
    if (Phaser.Input.Keyboard.JustDown(this._spaceKey) && !isInputLocked(this) && virtualInput.getAction()) {
      virtualInput.invokeAction();
    }

    if (this._exitTriggered || Date.now() < this._zoneGuardUntil) return;

    if (rectContains(UP_STAIRS_ZONE, fx, fy)) {
      this._exitTriggered = true;
      this.scene.start(SCENE.GAME_HALL, { spawnX: 750, spawnY: 590 });
    }
  }

  _depthSort() {
    this.natalka.setDepth(ySortDepth(this.natalka.feetY));
    if (this._presence) {
      for (const a of this._presence.avatars.values()) {
        a.sprite.setDepth(ySortDepth((a.sprite.feetY ?? a.sprite.y + HALF_H)));
      }
    }
  }
}
